import {
  Applicant,
  EntrataApplicationStage,
  EntrataApplicationStatus,
  Form,
  RelationshipType,
  WaitlistOffer,
  WaitlistStatus,
} from '@common/entities';
import { AxiosResponse } from 'axios';
import { destroy, get, post } from 'config/api/config';

import { AnyPage } from 'types/page';

export interface InvitationViewerSession {
  /** Stable warning codes that the viewer translates for the selected property locale. */
  prefillWarnings?: string[];
  applicant: Applicant;
  form: Omit<Form, 'pages'> & { pages?: AnyPage[] };
}

export interface PasswordlessInvitationEmail {
  isRegistered: boolean;
  email?: string;
}

export const resolvePasswordlessInvitationEmail = (
  orgId: number,
  propertyId: number,
  invitationToken: string,
): Promise<AxiosResponse<PasswordlessInvitationEmail>> =>
  post<PasswordlessInvitationEmail>('/viewer/auth/invitations/passwordless/email', {
    orgId,
    propertyId,
    invitationToken,
  });

export const acceptPasswordInvitation = (
  orgId: number,
  propertyId: number,
  invitationToken: string,
  newPassword: string,
): Promise<AxiosResponse<InvitationViewerSession>> =>
  post<InvitationViewerSession>('/viewer/auth/invitations/password', {
    orgId,
    propertyId,
    invitationToken,
    newPassword,
  });

export const acceptPasswordlessInvitation = (
  orgId: number,
  propertyId: number,
  invitationToken: string,
  verificationToken: string,
): Promise<AxiosResponse<InvitationViewerSession>> =>
  post<InvitationViewerSession>('/viewer/auth/invitations/passwordless', {
    orgId,
    propertyId,
    invitationToken,
    verificationToken,
  });

export const resumePasswordInvitation = (
  orgId: number,
  propertyId: number,
  invitationToken: string,
): Promise<AxiosResponse<InvitationViewerSession>> =>
  post<InvitationViewerSession>('/viewer/auth/invitations/password/resume', {
    orgId,
    propertyId,
    invitationToken,
  });

export const resumePasswordlessInvitation = (
  orgId: number,
  propertyId: number,
  invitationToken: string,
): Promise<AxiosResponse<InvitationViewerSession>> =>
  post<InvitationViewerSession>('/viewer/auth/invitations/passwordless/resume', {
    orgId,
    propertyId,
    invitationToken,
  });

const APPLICANT_APPLICATIONS = '/viewer/applicant/applications';
const APPLICANT_FORGOT_PASSWORD = '/viewer/applicant/forgot-password';
const APPLICANT_RESET_PASSWORD = '/viewer/applicant/resetpassword';
const LEASE_PACKET_PREFERENCES = '/viewer/document/lease-packet-preferences';
export const WAITLIST_ENDPOINT = '/viewer/applicant/waitlist';

export interface ApplicationStatusResponse {
  orgId: number;
  propertyName: string;
  propertyId: number;
  applicantId: string;
  relationshipType: RelationshipType;
  applicationId: number;
  moveInDate: string;
  applicationStatus: EntrataApplicationStatus;
  applicationStage: EntrataApplicationStage;
  name: string;
  applicantCompletedOn: string;
  isOnWaitlist?: boolean;
  waitlistStatus?: WaitlistStatus;
  responseId: string;
  applicationFormId: string;
  submissionStatus?: string | null;
  submissionStartedAt?: Date | null;
  submissionCompletedAt?: Date | null;
}

export interface LeasePacketPreference {
  propertyId: number;
  autoLeaseEnabled: boolean;
  packetId: number | null;
}

export const getLeasePacketPreferences = async (
  responseId: string,
): Promise<LeasePacketPreference[]> => {
  const response = await get<LeasePacketPreference[]>(
    `${LEASE_PACKET_PREFERENCES}/${encodeURIComponent(responseId)}`,
  );
  return response.data;
};

export const getApplicantApplications = async (viewerId?: string) => {
  const response = await get<ApplicationStatusResponse[]>(APPLICANT_APPLICATIONS, {
    params: { useCurrentProperty: true },
    ...(viewerId ? { headers: { 'x-form-viewer-id': viewerId } } : {}),
  });
  return response;
};

export const postResetPassword = async (
  token: string,
  orgId: number,
  propertyId: number,
  password: string,
) => {
  const response = await post(APPLICANT_RESET_PASSWORD, {
    token,
    orgId,
    propertyId,
    password,
  });
  return response;
};

export const requestForgotPassword = async (email: string, orgId: number, propertyId: number) => {
  const response = await post(APPLICANT_FORGOT_PASSWORD, {
    email,
    orgId,
    propertyId,
  });
  return response.data;
};

export const addToWaitlist = async (floorplanId: number) => {
  const response = await post(WAITLIST_ENDPOINT, {
    floorplanId,
  });
  return response;
};

export const removeFromWaitlist = async (floorplanId: number) => {
  const response = await destroy(`${WAITLIST_ENDPOINT}/${floorplanId}`);
  return response;
};

export const getWaitlistOffers = async (entrataApplicationId: number) => {
  const response = await get<WaitlistOffer>(WAITLIST_ENDPOINT, {
    params: { entrataApplicationId },
  });

  return response;
};

export const acceptWaitlistOffer = async (interestId: number) => {
  const response = await post(`${WAITLIST_ENDPOINT}/accept`, { interestId });
  return response;
};

export const declineWaitlistOffer = async (interestId: number) => {
  const response = await post(`${WAITLIST_ENDPOINT}/decline`, { interestId });
  return response;
};

export const isApplicationActive = (application: ApplicationStatusResponse) => {
  return (
    (application.applicationStatus === EntrataApplicationStatus.Started ||
      (application.applicationStatus === EntrataApplicationStatus.PartiallyCompleted &&
        application.applicantCompletedOn.length === 0)) &&
    application.applicationStage !== EntrataApplicationStage.Lease
  );
};
